/* eslint-disable @typescript-eslint/no-explicit-any */
import { GODIA_SALES_MODE } from "@constants";
import { t } from "i18next";
import { Virtual } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { useSnapshot } from "valtio";

import React from "react";
import { shallowEqual, useSelector } from "react-redux";

import { DynamicSvgComponent } from "@components/DynamicSvgComponent";

import { kioskStore } from "@pages/Kiosk/store";

import { LoyaltyCard } from "./LoyaltyCard";
import "./promo.css";
import {
    AvailableBalance,
    EmptyCodePromoContainer,
    GiftsContainer,
    GitsTitle,
} from "./styledCss";

import "swiper/swiper-bundle.min.css";

export default function GiftSection(): JSX.Element | null {
    const {
        project: {
            template: {
                pages: {
                    subSteps: {
                        customerBenefit: {
                            options: {
                                gifts,
                                rewards: { active: rewardsActive },
                            },
                        },
                    },
                },
            },
        },
        customerLanguage,
        isPrm,
    } = useSnapshot(kioskStore);
    const isGiftOnly = !rewardsActive;

    const { selectedSaleModeId } = useSnapshot(kioskStore);
    const userGifts = useSelector(
        (state: any) => state.orderSlice.userGifts,
        shallowEqual
    );

    const userGiftsForCurrentSaleMode = userGifts.filter((codePromo: any) =>
        codePromo.modeVente.includes(GODIA_SALES_MODE[selectedSaleModeId])
    );

    const availableGiftsTotal = userGiftsForCurrentSaleMode.length;

    return gifts.active ? (
        <GiftsContainer className="w-100" isGiftOnly={isGiftOnly} isPrm={isPrm}>
            <GiftSectionTitle
                giftsTitleMsg={gifts.languages[customerLanguage.name]}
                availableGiftsTotal={availableGiftsTotal}
            />
            <GiftsCardSwiperContainer
                isGiftOnly={isGiftOnly}
                userGiftsForCurrentSaleMode={userGiftsForCurrentSaleMode}
            />
        </GiftsContainer>
    ) : null;
}

type giftSectionTitleType = {
    giftsTitleMsg: string;
    availableGiftsTotal: string;
};

function GiftSectionTitle({
    giftsTitleMsg,
    availableGiftsTotal,
}: giftSectionTitleType) {
    return (
        <GitsTitle>
            <strong>{giftsTitleMsg} </strong>
            <AvailableBalance>
                {t("({{loyaltyAccount}} available)", {
                    loyaltyAccount: availableGiftsTotal,
                })}
            </AvailableBalance>
        </GitsTitle>
    );
}

type GiftsCardSwiperContainerType = {
    isGiftOnly: boolean;
    userGiftsForCurrentSaleMode: any[];
};

function GiftsCardSwiperContainer({
    isGiftOnly,
    userGiftsForCurrentSaleMode,
}: GiftsCardSwiperContainerType) {
    const { isPrm } = useSnapshot(kioskStore);
    const isEmptyCodePromo = userGiftsForCurrentSaleMode.length === 0;
    function computeDisplayHeight() {
        return isPrm && isGiftOnly ? "18rem" : isGiftOnly ? "40rem" : "7rem";
    }

    function computeSwiperSlideView() {
        return isPrm && isGiftOnly ? 2.5 : isGiftOnly ? 5.5 : 4.15;
    }
    return (
        <div
            className={`d-flex flex-row ${
                isEmptyCodePromo
                    ? "justify-content-center align-items-center"
                    : null
            }`}
        >
            {isEmptyCodePromo ? (
                <EmptyCodePromoContainer>
                    <DynamicSvgComponent
                        nameSvg="promoCode"
                        width={"100px"}
                        height={"100px"}
                    />
                    <span>{t("No gifts awarded")}</span>
                </EmptyCodePromoContainer>
            ) : (
                <Swiper
                    /* Only the visible gifts, plus a slide of margin either
                       side, are kept in the DOM. Without this the whole promo
                       code list was built and re-measured on every render, so
                       the display time grew with the number of gifts. */
                    modules={[Virtual]}
                    virtual={{
                        enabled: true,
                        addSlidesBefore: 1,
                        addSlidesAfter: 1,
                    }}
                    slidesPerView={computeSwiperSlideView()}
                    spaceBetween={5}
                    direction={isGiftOnly ? "vertical" : "horizontal"}
                    className="position-absolute w-100 h-100"
                    style={{
                        maxWidth: "61.5rem",
                        maxHeight: computeDisplayHeight(),
                        boxShadow: isGiftOnly
                            ? "inset 0px -2px 9px -7px rgba(0, 0, 0, 0.4), inset 0px 2px 9px -7px rgba(0, 0, 0, 0.4)"
                            : "inset -2px 0px 9px -7px rgba(0, 0, 0, 0.4), inset 2px 0px 9px -7px rgba(0, 0, 0, 0.4)",
                        margin: "0 0.5rem",
                        padding: "0.5rem",
                    }}
                >
                    {userGiftsForCurrentSaleMode.map(
                        (couponData: any, index: number) => {
                            return (
                                <SwiperSlide
                                    key={index}
                                    virtualIndex={index}
                                    className="product-swiper-slide"
                                >
                                    <LoyaltyCard
                                        key={index}
                                        couponData={couponData}
                                        isPromoCode={true}
                                    />
                                </SwiperSlide>
                            );
                        }
                    )}
                </Swiper>
            )}
        </div>
    );
}
